import type { AppError, DatabaseError, AuthError } from '../types';

/**
 * Custom error classes for different types of application errors
 */
export class AppErrorClass extends Error implements AppError {
  code: string;
  details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.details = details;
  }
}

export class DatabaseErrorClass extends AppErrorClass implements DatabaseError {
  query?: string;
  table?: string;

  constructor(
    code: string, 
    message: string, 
    query?: string, 
    table?: string, 
    details?: Record<string, unknown>
  ) {
    super(code, message, details);
    this.name = 'DatabaseError';
    this.query = query;
    this.table = table;
  }
}

export class AuthErrorClass extends AppErrorClass implements AuthError {
  provider?: string;

  constructor(
    code: string, 
    message: string, 
    provider?: string, 
    details?: Record<string, unknown>
  ) {
    super(code, message, details);
    this.name = 'AuthError';
    this.provider = provider;
  }
}

/**
 * Error codes for consistent error handling
 */
export const ERROR_CODES = {
  // Authentication errors
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  AUTH_SIGNUP_FAILED: 'AUTH_SIGNUP_FAILED',
  
  // Database errors
  DB_CONNECTION_FAILED: 'DB_CONNECTION_FAILED',
  DB_QUERY_FAILED: 'DB_QUERY_FAILED',
  DB_FUNCTION_NOT_FOUND: 'DB_FUNCTION_NOT_FOUND',
  DB_PERMISSION_DENIED: 'DB_PERMISSION_DENIED',
  
  // Data errors
  DATA_NOT_FOUND: 'DATA_NOT_FOUND',
  DATA_INVALID_FORMAT: 'DATA_INVALID_FORMAT',
  DATA_PROCESSING_FAILED: 'DATA_PROCESSING_FAILED',
  
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  
  // Validation errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  
  // General errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',
} as const;

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES = {
  [ERROR_CODES.AUTH_REQUIRED]: 'Please sign in to access this feature.',
  [ERROR_CODES.AUTH_INVALID_CREDENTIALS]: 'Invalid email or password. Please try again.',
  [ERROR_CODES.AUTH_SESSION_EXPIRED]: 'Your session has expired. Please sign in again.',
  [ERROR_CODES.AUTH_SIGNUP_FAILED]: 'Failed to create account. Please try again.',
  
  [ERROR_CODES.DB_CONNECTION_FAILED]: 'Unable to connect to the database. Please try again later.',
  [ERROR_CODES.DB_QUERY_FAILED]: 'Failed to retrieve data. Please try again.',
  [ERROR_CODES.DB_FUNCTION_NOT_FOUND]: 'Database function not available. Using alternative method.',
  [ERROR_CODES.DB_PERMISSION_DENIED]: 'You do not have permission to access this data.',
  
  [ERROR_CODES.DATA_NOT_FOUND]: 'No data found for the selected criteria.',
  [ERROR_CODES.DATA_INVALID_FORMAT]: 'The data format is invalid or corrupted.',
  [ERROR_CODES.DATA_PROCESSING_FAILED]: 'Failed to process the data. Please try again.',
  
  [ERROR_CODES.NETWORK_ERROR]: 'Network connection error. Please check your internet connection.',
  [ERROR_CODES.NETWORK_TIMEOUT]: 'Request timed out. Please try again.',
  
  [ERROR_CODES.VALIDATION_FAILED]: 'Please check your input and try again.',
  [ERROR_CODES.VALIDATION_REQUIRED_FIELD]: 'This field is required.',
  
  [ERROR_CODES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
  [ERROR_CODES.FEATURE_NOT_AVAILABLE]: 'This feature is not available at the moment.',
} as const;

/**
 * Parse Supabase errors and convert to application errors
 */
export function parseSupabaseError(error: unknown): AppErrorClass {
  if (!error) {
    return new AppErrorClass(ERROR_CODES.UNKNOWN_ERROR, ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR]);
  }

  const errorObj = error as Record<string, unknown>;
  const message = (errorObj.message as string) || 'Unknown error occurred';
  const code = (errorObj.code as string) || ERROR_CODES.UNKNOWN_ERROR;

  // Authentication errors
  if (code === 'invalid_credentials' || message.includes('Invalid login credentials')) {
    return new AuthErrorClass(ERROR_CODES.AUTH_INVALID_CREDENTIALS, ERROR_MESSAGES[ERROR_CODES.AUTH_INVALID_CREDENTIALS]);
  }

  if (code === 'signup_disabled' || message.includes('signup')) {
    return new AuthErrorClass(ERROR_CODES.AUTH_SIGNUP_FAILED, ERROR_MESSAGES[ERROR_CODES.AUTH_SIGNUP_FAILED]);
  }

  // Database errors
  if (code === 'PGRST116' || message.includes('No rows found')) {
    return new DatabaseErrorClass(ERROR_CODES.DATA_NOT_FOUND, ERROR_MESSAGES[ERROR_CODES.DATA_NOT_FOUND]);
  }

  if (code === 'PGRST202' || message.includes('permission denied')) {
    return new DatabaseErrorClass(ERROR_CODES.DB_PERMISSION_DENIED, ERROR_MESSAGES[ERROR_CODES.DB_PERMISSION_DENIED]);
  }

  if (message.includes('function') && message.includes('does not exist')) {
    return new DatabaseErrorClass(ERROR_CODES.DB_FUNCTION_NOT_FOUND, ERROR_MESSAGES[ERROR_CODES.DB_FUNCTION_NOT_FOUND]);
  }

  // Network errors
  if (message.includes('network') || message.includes('fetch')) {
    return new AppErrorClass(ERROR_CODES.NETWORK_ERROR, ERROR_MESSAGES[ERROR_CODES.NETWORK_ERROR]);
  }

  if (message.includes('timeout')) {
    return new AppErrorClass(ERROR_CODES.NETWORK_TIMEOUT, ERROR_MESSAGES[ERROR_CODES.NETWORK_TIMEOUT]);
  }

  // Default to database error for most Supabase errors
  return new DatabaseErrorClass(ERROR_CODES.DB_QUERY_FAILED, message);
}

/**
 * Retry mechanism for failed operations
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Don't retry authentication or permission errors
      if (lastError instanceof AuthErrorClass || 
          (lastError instanceof DatabaseErrorClass && lastError.code === ERROR_CODES.DB_PERMISSION_DENIED)) {
        throw lastError;
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }

  throw lastError!;
}

/**
 * Log errors for debugging and monitoring
 */
export function logError(error: Error, context?: Record<string, unknown>): void {
  const errorInfo = {
    name: error.name,
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    context,
  };

  // In development, log to console
  if (import.meta.env.DEV) {
    console.error('Application Error:', errorInfo);
  }

  // In production, you might want to send to an error tracking service
  // Example: Sentry, LogRocket, etc.
  if (import.meta.env.PROD) {
    // sendToErrorTrackingService(errorInfo);
  }
}

/**
 * Create a safe error handler for React components
 */
export function createErrorHandler(
  setError: (error: Error | null) => void,
  setLoading?: (loading: boolean) => void
) {
  return (error: unknown, context?: Record<string, unknown>) => {
    const appError = error instanceof AppErrorClass 
      ? error 
      : parseSupabaseError(error);

    logError(appError, context);
    setError(appError);
    
    if (setLoading) {
      setLoading(false);
    }
  };
}

/**
 * Check if an error is recoverable (user can retry)
 */
export function isRecoverableError(error: Error): boolean {
  if (error instanceof AppErrorClass) {
    const nonRecoverableCodes = [
      ERROR_CODES.AUTH_INVALID_CREDENTIALS,
      ERROR_CODES.DB_PERMISSION_DENIED,
      ERROR_CODES.VALIDATION_FAILED,
      ERROR_CODES.DATA_INVALID_FORMAT,
    ];
    
    return !nonRecoverableCodes.includes(error.code as (typeof nonRecoverableCodes)[number]);
  }

  return true;
}

/**
 * Get appropriate user action for an error
 */
export function getErrorAction(error: Error): {
  action: 'retry' | 'signin' | 'contact_support' | 'none';
  message: string;
} {
  if (error instanceof AuthErrorClass) {
    return {
      action: 'signin',
      message: 'Please sign in to continue.',
    };
  }

  if (error instanceof AppErrorClass) {
    if (isRecoverableError(error)) {
      return {
        action: 'retry',
        message: 'Please try again.',
      };
    }

    if (error.code === ERROR_CODES.DB_PERMISSION_DENIED) {
      return {
        action: 'contact_support',
        message: 'Please contact support for assistance.',
      };
    }
  }

  return {
    action: 'retry',
    message: 'Please try again.',
  };
}